import { useState } from 'react';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { finaliseRepairBucket } from '../../lib/api';
import { formatDisplayLong } from '../../lib/dateUtils';
import { getElementTypeLabel } from '../../constants/AssetTestConfig';
import { downloadRepairBucketReportPdf } from '../../lib/downloadBlob';

/**
 * Confirms finalising an open repair bucket into a signed-off report.
 * Generating the report IS the finalise action (Brief 59 decision — there is
 * no separate "finish repairs" step), so this is the one guard against an
 * accidental, irreversible, report-number-consuming click.
 *
 * Props:
 *   isOpen  — whether the modal is visible
 *   onClose — called after a successful finalise, or when the user cancels
 *   bucket  — the open bucket being finalised: { siteId, siteName, elementType,
 *             repairCount, earliestRepairedAt, latestRepairedAt } (an open
 *             bucket has no `id` — see reportRows.js)
 */
export default function FinaliseRepairsModal({ isOpen, onClose, bucket }) {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [workOrderNumber, setWorkOrderNumber] = useState('');

  const handleClose = () => {
    if (isSubmitting) return; // round trip in progress — same guard as the disabled confirm button
    setError('');
    setWorkOrderNumber('');
    onClose();
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setError('');

    const trimmedWorkOrder = workOrderNumber.trim();
    let created;
    try {
      created = await finaliseRepairBucket({
        siteId: bucket.siteId,
        elementType: bucket.elementType,
        ...(trimmedWorkOrder ? { workOrderNumber: trimmedWorkOrder } : {}),
      });
    } catch (err) {
      // The backend sends no `code` on 409 — both no_repairs and concurrent_finalise
      // are `{ error: '…' }` only — so treat every 409 the same: the open list is
      // stale and a refetch is always safe.
      if (err.response?.status === 409) {
        queryClient.invalidateQueries('open-repair-buckets-for-reports');
      }
      setError(err.response?.data?.error || 'Failed to finalise repair report');
      setIsSubmitting(false);
      return;
    }

    // The report number is spent the instant the row above returns — this is no longer
    // an "open bucket that might fail" from here on, regardless of what the PDF fetch does.
    queryClient.invalidateQueries('repair-buckets-for-reports');
    queryClient.invalidateQueries('open-repair-buckets-for-reports');

    let downloadFailed = false;
    try {
      await downloadRepairBucketReportPdf(created.id);
    } catch (err) {
      console.error('Repair bucket finalised but PDF download failed', err);
      downloadFailed = true;
    }

    // The finalise itself always succeeded — say so unconditionally, then separately
    // flag a download failure. Folding both into one failed-looking toast would invite
    // someone to click Generate again on a bucket that no longer has anything to finalise.
    toast.success(downloadFailed ? 'Repair report created.' : 'Repair report created and downloaded.');
    if (downloadFailed) {
      toast.error('The download failed. Use the Repair PDF button on this row to try again.');
    }

    setWorkOrderNumber('');
    setIsSubmitting(false);
    onClose();
  };

  if (!isOpen || !bucket) return null;

  const componentLabel = getElementTypeLabel(bucket.elementType).toLowerCase();
  const hasDateRange = bucket.earliestRepairedAt && bucket.latestRepairedAt;
  const dateRange = hasDateRange
    ? `${formatDisplayLong(bucket.earliestRepairedAt)} – ${formatDisplayLong(bucket.latestRepairedAt)}`
    : '';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create repair report" className="finalise-repairs-modal">
      <p>
        This creates the report for <strong>{bucket.repairCount}</strong> {componentLabel} repair
        {bucket.repairCount === 1 ? '' : 's'} at <strong>{bucket.siteName}</strong>
        {dateRange ? ` (${dateRange})` : ''}.
      </p>
      <p>
        Creating the report closes these repairs into it. This cannot be undone, and you will be named as the
        signatory on the certificate.
      </p>
      <div className="form-group">
        <label htmlFor="finalise-work-order">Work Order #</label>
        <input
          id="finalise-work-order"
          type="text"
          className="form-control"
          value={workOrderNumber}
          onChange={(e) => setWorkOrderNumber(e.target.value)}
          placeholder="e.g. WO-12345"
          maxLength={100}
          disabled={isSubmitting}
        />
        <span className="form-hint">{'Printed on the certificate. Leave blank if there isn\'t one.'}</span>
      </div>
      {error && <p className="form-error" role="alert">{error}</p>}
      <div className="form-actions">
        <Button type="button" onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="button" variant="primary" onClick={handleConfirm} disabled={isSubmitting}>
          {isSubmitting ? 'Creating…' : 'Create report'}
        </Button>
      </div>
    </Modal>
  );
}
